import type {
  TravellerMembershipDTO,
  TravellerWithCollectionMembershipsDTO,
} from '@/api/trafikportalen';
import { getTravellerStatus } from '@/helpers/status';

export interface TravellerFacetFilters {
  department?: { id: string }[] | null;
  status?: { id: string }[] | null;
  allpayStatus?: { id: string }[] | null;
}

export interface TravellerFacetItem {
  id: string;
  memberships: TravellerMembershipDTO[];
  allpayStatus: string;
}

export const getTravellerFacetFilterKey = (
  filters?: TravellerFacetFilters | null,
): string => {
  const toKey = (items?: { id: string }[] | null) =>
    (items ?? [])
      .map((item) => item.id)
      .sort()
      .join(',');

  return [
    toKey(filters?.department),
    toKey(filters?.status),
    toKey(filters?.allpayStatus),
  ].join('|');
};

export const filterTravellersByFacets = <T extends TravellerFacetItem>(
  travellers: T[],
  filters?: TravellerFacetFilters | null,
): T[] => {
  let filtered = travellers;

  if (filters?.department && filters.department.length > 0) {
    filtered = filtered.filter((traveller) =>
      filters.department?.some((department) =>
        traveller.memberships.some(
          (membership) => membership.department_id === department.id,
        ),
      ),
    );
  }

  if (filters?.status && filters.status.length > 0) {
    filtered = filtered.filter((traveller) => {
      const travellerStatus = getTravellerStatus({
        traveller: { id: traveller.id },
        memberships: traveller.memberships,
      } as TravellerWithCollectionMembershipsDTO);
      return filters.status?.some((status) => status.id === travellerStatus);
    });
  }

  if (filters?.allpayStatus && filters.allpayStatus.length > 0) {
    filtered = filtered.filter((traveller) =>
      filters.allpayStatus?.some(
        (status) => status.id === traveller.allpayStatus,
      ),
    );
  }

  return filtered;
};

export const pruneSelectedIds = (
  selectedIds: Iterable<string>,
  allowedIds: Iterable<string>,
): string[] => {
  const allowed = allowedIds instanceof Set ? allowedIds : new Set(allowedIds);
  const next: string[] = [];

  for (const id of selectedIds) {
    if (allowed.has(id)) {
      next.push(id);
    }
  }

  return next;
};
